/* PiHOT product lists loaded after the page:
   - <p-recommendations>  sections/pihot-product-recommendations.liquid, rendered through
     the product recommendations route.
   - <p-recently-viewed>  sections/pihot-recently-viewed.liquid, handles from Vision's
     localStorage list, cards from templates/search.pihot-recently-viewed.liquid.
   Both fetch when they near the viewport, swap the skeleton for real cards, hand them
   to the motion layer, and hide the whole section when there is nothing to show. */

(() => {
  const whenNear = (el, callback) => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        observer.disconnect();
        callback();
      },
      { rootMargin: '0px 0px 600px 0px' }
    );
    observer.observe(el);
    return observer;
  };

  const sectionOf = (el) => el.closest('.shopify-section') || el.closest('.p-recs');

  const reveal = (el) => {
    window.PiHOT?.motion?.init(el);
    window.ScrollTrigger?.refresh();
  };

  const hide = (el) => {
    sectionOf(el).hidden = true;
    window.ScrollTrigger?.refresh();
  };

  if (!customElements.get('p-recommendations')) {
    class PRecommendations extends HTMLElement {
      connectedCallback() {
        if (this.querySelector('[data-p-recs-loaded]')) return;
        this.observer = whenNear(this, () => this.load());
      }

      disconnectedCallback() {
        this.observer?.disconnect();
      }

      async load() {
        try {
          const response = await fetch(this.dataset.url);
          if (!response.ok) throw new Error(`Recommendations request failed: ${response.status}`);

          const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
          const grid = doc.querySelector('p-recommendations [data-p-recs-loaded]');
          if (!grid) return hide(this);

          this.replaceChildren(document.adoptNode(grid));
          reveal(this);
        } catch (error) {
          console.error(error);
          hide(this);
        }
      }
    }

    customElements.define('p-recommendations', PRecommendations);
  }

  if (!customElements.get('p-recently-viewed')) {
    class PRecentlyViewed extends HTMLElement {
      connectedCallback() {
        if (this.loaded) return;

        const limit = parseInt(this.dataset.limit, 10) || 4;
        const exclude = new Set((this.dataset.exclude || '').split(',').filter(Boolean));
        let viewed = [];
        try {
          viewed = JSON.parse(window.localStorage.getItem('recently-viewed')) || [];
        } catch (error) {
          viewed = [];
        }
        this.handles = viewed.filter((handle) => handle && !exclude.has(handle)).slice(0, limit);

        if (!this.handles.length) {
          if (!window.Shopify?.designMode) hide(this);
          return;
        }

        this.querySelectorAll('.p-recs__skeleton').forEach((skeleton, index) => {
          if (index >= this.handles.length) skeleton.remove();
        });
        this.observer = whenNear(this, () => this.load());
      }

      disconnectedCallback() {
        this.observer?.disconnect();
      }

      async load() {
        const query = this.handles.map((handle) => `handle:${handle}`).join(' OR ');

        try {
          const response = await fetch(`${this.dataset.url}&q=${encodeURIComponent(query)}`);
          if (!response.ok) throw new Error(`Recently viewed request failed: ${response.status}`);

          const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
          const items = [...doc.querySelectorAll('[data-p-recently-viewed] > [data-p-handle]')].sort(
            (a, b) => this.handles.indexOf(a.dataset.pHandle) - this.handles.indexOf(b.dataset.pHandle)
          );
          if (!items.length) return hide(this);

          const grid = this.querySelector('.p-fc__grid');
          grid.replaceChildren(...items.map((item) => document.adoptNode(item)));
          grid.removeAttribute('aria-hidden');
          grid.setAttribute('role', 'list');
          this.loaded = true;
          reveal(this);
        } catch (error) {
          console.error(error);
          hide(this);
        }
      }
    }

    customElements.define('p-recently-viewed', PRecentlyViewed);
  }
})();
